import { useQuery } from '@tanstack/react-query';
import { graphqlClient } from '@/utils/graphqlClient';
import { gql } from 'graphql-request';
import { useDispatch } from 'react-redux';
import { setCompany, Company as ReduxCompany, PricingMetric as ReduxPricingMetric, CompanyPage } from '@/store/companySlice';
import { setGraphQLData, GraphQLCompanyData } from '@/store/graphqlSlice';
import { useEffect } from 'react';

// Define company data types
interface PricingMetric {
  id: string;
  display_name: string;
  modality?: string;
  sample_values?: string[];
}

interface Charge {
  id: string;
  type: string;
  currency: string | null;
  price: number | null;
  notes?: string;
  price_per?: string;
  included_quantity?: number | null;
  overage_price?: string | null;
  overage_quantity?: number | null;
  minimum_price?: string | null;
  pricing_metric?: PricingMetric;
}

interface Discount {
  id: string;
  type: string;
  amount: number;
  description?: string;
  duration?: string;
  applicable_charge_types?: string[];
}

interface Plan {
  id: string;
  display_name: string;
  add_on: boolean;
  plan_name: string;
  charges: Charge[];
  discounts?: Discount[];
}

interface Quarter {
  quarter: string;
  version: string;
  ids: string[];
  screenshot_url?: string;
  verified?: boolean;
}

interface Scrape {
  version: string;
  quarter: string;
  screenshot_url?: string;
  cloudinary_url?: string;
  markdown_url?: string;
  html_url?: string;
  verified?: boolean;
}

interface Tag {
  id: string;
  name: string;
  description?: string;
}

interface Threshold {
  id: string;
  display_name: string;
  sample_values?: any[];
}

interface Page {
  id: string;
  subslug: string;
  tags?: Tag[];
  thresholds?: Threshold[];
  pricing_metrics?: PricingMetric[];
  quarters?: Quarter[];
  scrapes?: Scrape[];
  pricing_page_url?: string;
  plans?: Plan[];
}

export interface Company {
  id: string;
  slug: string;
  employees?: number;
  category?: string;
  logo_url?: string;
  description?: string;
  url?: string;
  scraper?: string;
  pages?: Page[];
}

interface CompanyResponse {
  company: Company;
}

// Define the expanded query with all the details we need
const EXPANDED_QUERY = gql`
  query GetCompany($slug: String!) {
    company(slug: $slug) {
      id
      slug
      employees
      category
      logo_url
      description
      url
      scraper
      pages {
        id
        subslug
        tags {
          id
          name
          description
        }
        thresholds {
          id
          display_name
          sample_values
        }
        pricing_metrics {
          id
          display_name
          modality
          sample_values
        }
        quarters {
          quarter
          version
          ids
          screenshot_url
          verified
        }
        scrapes {
          version
          quarter
          screenshot_url
          cloudinary_url
          markdown_url
          html_url
          verified
        }
        pricing_page_url
        plans {
          id
          display_name
          add_on
          plan_name
          charges {
            id
            type
            currency
            price
            notes
            price_per
            included_quantity
            overage_price
            overage_quantity
            minimum_price
            pricing_metric {
              id
              display_name
              modality
            }
          }
          discounts {
            id
            type
            amount_type
            amount
            description
            duration
            applicable_charge_types
          }
        }
      }
    }
  }
`;

interface UseCompanyDataOptions {
  slug: string;
  enabled?: boolean;
  forceRefetch?: boolean;
}

/**
 * A shared hook to fetch and manage company data using React Query
 * This serves as the single source of truth for company data
 */
/**
 * Maps API data to Redux format
 */
function mapApiToReduxFormat(apiData: Company): ReduxCompany {
  // Transform pages
  const pages: CompanyPage[] = apiData.pages?.map(page => ({
    id: page.id,
    subslug: page.subslug,
    pricingPageUrl: page.pricing_page_url || null,
    screenshotUrl: page.scrapes?.[0]?.screenshot_url || null,
    screenshotUrlVersion: page.scrapes?.[0]?.version || null,
    pricing_metrics: page.pricing_metrics || [],
    pricingMetrics: page.pricing_metrics || [],
    scrapes: page.scrapes || [],
    tags: page.tags || [],
  })) || [];
  
  // Transform pricing metrics
  const pricingMetrics: ReduxPricingMetric[] = [];
  apiData.pages?.forEach(page => {
    page.pricing_metrics?.forEach(metric => {
      pricingMetrics.push({
        id: metric.id,
        slug: metric.display_name.toLowerCase().replace(/ /g, '-'),
        display_name: metric.display_name,
        modality: metric.modality || null,
        subcategory: null,
        sample_values: metric.sample_values || null,
        sample_prices: null,
        created_at: null
      });
    });
  });
  
  return {
    id: apiData.id,
    slug: apiData.slug,
    logoUrl: apiData.logo_url || null,
    employees: apiData.employees?.toString() || '',
    category: apiData.category || '',
    description: apiData.description || null,
    url: apiData.url || null,
    legacySlug: null,
    pricingMetrics,
    pages,
    scraper: apiData.scraper
  };
}

/**
 * A shared hook to fetch and manage company data using React Query
 * This serves as the single source of truth for company data
 */
export function useCompanyData({ 
  slug, 
  enabled = true,
  forceRefetch = false 
}: UseCompanyDataOptions) {
  const queryKey = ['company', slug];
  
  const dispatch = useDispatch();
  
  // Use React Query as the single source of truth
  const queryResult = useQuery<Company>({
    queryKey,
    queryFn: async () => {
      console.log(`🔄 [useCompanyData] Fetching data for ${slug}`);
      const response = await graphqlClient.request<CompanyResponse>(EXPANDED_QUERY, { slug });
      console.log(`✅ [useCompanyData] Successfully loaded data for ${slug}`);
      return response.company;
    },
    enabled: !!slug && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - match the previous cache expiry
    refetchOnWindowFocus: false,
    refetchOnMount: !forceRefetch ? 'always' : false
  });
  
  // Sync with Redux store when data changes
  useEffect(() => {
    if (queryResult.data) {
      console.log(`🔄 [useCompanyData] Updating Redux store with data for ${slug}`);
      
      // Map to Redux format and update company slice
      const reduxFormattedData = mapApiToReduxFormat(queryResult.data);
      dispatch(setCompany(reduxFormattedData));
      
      // Also update graphqlSlice for backward compatibility
      // Convert to GraphQLCompanyData format which expects employees as a string
      const graphqlData: GraphQLCompanyData = {
        ...queryResult.data,
        employees: queryResult.data.employees?.toString() || null
      };
      dispatch(setGraphQLData(graphqlData));
    }
  }, [queryResult.data, dispatch, slug]);
  
  return queryResult;
}
